import type { CSSProperties, ReactElement, ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { palette, radius, space, typography } from "@ux/tokens";
import { useViewport } from "@ux/hooks";

export interface AdminNavItem {
    readonly to: string;
    readonly label: string;
    readonly icon: ReactNode;
    readonly end?: boolean;
    /**
     * Optional shorter label used by the phone bottom tab bar where
     * horizontal real estate per tab is ~70px. Falls back to `label`
     * when omitted; long labels get visually truncated with ellipsis.
     */
    readonly shortLabel?: string;
}

export interface AdminShellProps {
    readonly title: string;
    readonly navItems: readonly AdminNavItem[];
    readonly footer?: ReactNode;
    /**
     * When false, the desktop sidebar and phone tab bar are omitted and
     * content fills the viewport. Used for non-Editor sessions that should
     * not see Office-admin navigation.
     */
    readonly showNav?: boolean;
}

/**
 * Two physical layouts share this one composition:
 *
 *   - Tablet & desktop: a 240-px left sidebar with title, vertical nav,
 *     and the original footer slot. Identical to the pre-responsive
 *     shell.
 *
 *   - Phone: the sidebar disappears entirely, content fills the viewport,
 *     and a sticky bottom tab bar carries primary navigation. This is
 *     the iOS-native pattern — always-visible navigation in the thumb
 *     zone — and it directly addresses the "240-px sidebar eats the
 *     screen" pain point reported on iPhone.
 *
 * The two variants are encapsulated in dedicated sub-components so the
 * desktop code path is byte-identical to its pre-responsive form. Easy
 * to review, easy to revert one branch without touching the other.
 */
export function AdminShell({ showNav = true, ...props }: AdminShellProps): ReactElement {
    const { isPhone } = useViewport();
    if (!showNav) {
        return <NavlessAdminShell />;
    }
    return isPhone ? <PhoneAdminShell {...props} /> : <DesktopAdminShell {...props} />;
}

const navlessLayoutStyle: CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
};

function NavlessAdminShell(): ReactElement {
    return (
        <div style={navlessLayoutStyle}>
            <section style={phoneContentStyle}>
                <Outlet />
            </section>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Desktop / tablet variant — preserved from the original AdminShell.
// ──────────────────────────────────────────────────────────────────────

const desktopLayoutStyle: CSSProperties = {
    flex: 1,
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    minHeight: 0,
};

const sidebarStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    padding: space[4],
    gap: space[4],
    borderRight: `1px solid ${palette.border}`,
    background: palette.surface,
};

const desktopContentStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
};

const navListStyle: CSSProperties = {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: space[1],
};

const titleStyle: CSSProperties = {
    margin: 0,
    fontSize: typography.size.lg,
    fontWeight: typography.weight.bold,
    letterSpacing: "-0.01em",
};

function DesktopAdminShell({ title, navItems, footer }: AdminShellProps): ReactElement {
    return (
        <div style={desktopLayoutStyle}>
            <aside style={sidebarStyle} aria-label="Admin navigation">
                <h2 style={titleStyle}>{title}</h2>
                <nav>
                    <ul style={navListStyle}>
                        {navItems.map((item) => (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    end={item.end ?? false}
                                    style={({ isActive }) => sidebarLinkStyle(isActive)}
                                >
                                    <span style={sidebarIconWrapStyle}>{item.icon}</span>
                                    <span>{item.label}</span>
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>
                {footer !== undefined ? (
                    <div
                        style={{
                            marginTop: "auto",
                            color: palette.fgSubtle,
                            fontSize: typography.size.xs,
                        }}
                    >
                        {footer}
                    </div>
                ) : null}
            </aside>
            <section style={desktopContentStyle}>
                <Outlet />
            </section>
        </div>
    );
}

function sidebarLinkStyle(isActive: boolean): CSSProperties {
    return {
        display: "flex",
        alignItems: "center",
        gap: space[2],
        padding: `${space[2]} ${space[3]}`,
        borderRadius: radius.md,
        color: isActive ? palette.fg : palette.fgMuted,
        textDecoration: "none",
        fontSize: typography.size.sm,
        fontWeight: isActive ? typography.weight.semibold : typography.weight.medium,
        background: isActive ? palette.accentBg : "transparent",
        border: `1px solid ${isActive ? "rgba(255,255,255,0.10)" : "transparent"}`,
        transition: "background 120ms ease, color 120ms ease",
    };
}

const sidebarIconWrapStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
};

// ──────────────────────────────────────────────────────────────────────
// Phone variant — sidebar replaced by a sticky bottom tab bar.
// ──────────────────────────────────────────────────────────────────────

const phoneLayoutStyle: CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
};

const phoneContentStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
};

const phoneTabBarStyle: CSSProperties = {
    flexShrink: 0,
    display: "flex",
    alignItems: "stretch",
    borderTop: `1px solid ${palette.border}`,
    background: palette.surface,
    // Match BottomActionBar so the tab bar always clears the iOS home
    // indicator and gestures don't accidentally trigger nav.
    paddingBottom: "env(safe-area-inset-bottom)",
};

const phoneTabBarListStyle: CSSProperties = {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flex: 1,
    minHeight: 0,
};

function PhoneAdminShell({ navItems }: AdminShellProps): ReactElement {
    return (
        <div style={phoneLayoutStyle}>
            <section style={phoneContentStyle}>
                <Outlet />
            </section>
            <nav style={phoneTabBarStyle} aria-label="Admin navigation">
                <ul style={phoneTabBarListStyle}>
                    {navItems.map((item) => (
                        <li key={item.to} style={{ flex: 1, display: "flex" }}>
                            <NavLink
                                to={item.to}
                                end={item.end ?? false}
                                style={({ isActive }) => phoneTabLinkStyle(isActive)}
                            >
                                <span style={phoneTabIconWrapStyle}>{item.icon}</span>
                                <span style={phoneTabLabelStyle}>
                                    {item.shortLabel ?? item.label}
                                </span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>
        </div>
    );
}

function phoneTabLinkStyle(isActive: boolean): CSSProperties {
    return {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        padding: `${space[2]} ${space[1]}`,
        textDecoration: "none",
        color: isActive ? palette.accentFg : palette.fgMuted,
        background: isActive ? palette.accentBg : "transparent",
        fontSize: typography.size.xs,
        fontWeight: isActive ? typography.weight.semibold : typography.weight.medium,
        // Cap to 56px to keep the bar visually balanced; the safe-area
        // padding on the parent <nav> sits below this.
        minHeight: 56,
    };
}

const phoneTabIconWrapStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
};

const phoneTabLabelStyle: CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    lineHeight: 1.2,
};
